import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import { useBrand } from "../../lib/BrandContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import PasswordInput from "../../components/PasswordInput";
import LegalLinks from "../../components/LegalLinks";

export default function Login() {
  const { login } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Default checked — matches existing app behavior (tokens persisted in
  // localStorage), so no user is unexpectedly logged out by this feature.
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password, remember);
      const rawNext = params.get("next");
      // Only allow same-origin internal paths (defence against open-redirect)
      const isSafeNext = rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//");
      const target = (isSafeNext ? rawNext : null) || location.state?.from || null;
      if (target) navigate(target);
      else if (user.role === "coach") navigate("/coach/dashboard");
      else if (user.role === "parent") navigate("/parent/dashboard");
      else if (user.role === "platform_admin") navigate("/admin/dashboard");
      else navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not log in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-ink-100/40">
      <div className="m-auto w-full max-w-md px-6 py-10">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-ink-900" data-testid="login-brand">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white shadow-sm">
            <ShieldCheck className="h-5 w-5" />
          </span>
          {brand.app_name}
        </Link>

        <div className="mt-8 rounded-2xl border border-ink-100 bg-white p-8 shadow-sm">
          {params.get("verified") === "1" ? (
            <div
              className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
              data-testid="login-verified-banner"
            >
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-bold text-white">✓</span>
              <span>
                <span className="font-semibold">Your email has been verified.</span>
                {params.get("switched") === "1" ? (
                  <>
                    {" "}For your privacy we signed out the previous account.
                    Please log in with the account you just verified.
                  </>
                ) : (
                  <> Please log in to continue.</>
                )}
              </span>
            </div>
          ) : null}
          <h1 className="font-heading text-2xl font-bold text-ink-900">Welcome back</h1>
          <p className="mt-1 text-sm text-ink-500">Log in to manage your teams and dues.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-12"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-12"
                data-testid="login-password-input"
                capsWarningTestId="login-caps-warning"
              />
            </div>

            <label
              className="flex select-none items-center gap-2 text-sm text-ink-700"
              data-testid="login-remember-label"
            >
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-ink-300 text-brand focus:ring-brand"
                data-testid="login-remember-checkbox"
              />
              Remember me
            </label>

            {error ? (
              <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">
                {error}
              </p>
            ) : null}

            <Button type="submit" disabled={loading} className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
            </Button>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm">
            <Link to="/forgot-password" className="font-semibold text-brand hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
            <Link to="/signup" className="text-ink-700 hover:text-ink-900" data-testid="login-to-signup">
              Create account
            </Link>
          </div>
        </div>
        <LegalLinks testid="login-legal-links" />
      </div>
    </div>
  );
}
